import axios, { AxiosResponse } from "axios";
import TokenManagerHelper from "./token_manager_helper";

// Configuração da URL base da API DAV
const getDAVApiBaseURL = (): string => {
  if (process.env.REACT_APP_API_URL) {
    return `${process.env.REACT_APP_API_URL}/api/v1`;
  }

  if (process.env.NODE_ENV === "development") {
    return "http://localhost:3000/api/v1";
  }

  return "https://appdav-qa.coffcode.com/api/v1";
};

const DAV_API_BASE_URL = getDAVApiBaseURL();

export interface DavApiData {
  user: {
    documentOwner: string;
    id: number;
    name: string;
    user: string;
    password: string;
    lastSyncDate: string;
  };
  customers: Array<{
    documentOwner: string;
    id: number;
    name: string;
  }>;
  paymentMethods: Array<{
    documentOwner: string;
    id: number;
    description: string;
    internalCode: number;
  }>;
  paymentForms: Array<{
    documentOwner: string;
    id: number;
    description: string;
    numberInstallments: number;
    interest: number;
    dayFirstQuote: number;
  }>;
  products: Array<{
    documentOwner: string;
    id: number;
    description: string;
    quantity: number;
    unitPrice: number;
    promotionPrice: number;
    promotionFinalDate: string;
  }>;
}

class DavApiService {
  private readonly baseURL: string;

  constructor(baseURL: string = DAV_API_BASE_URL) {
    this.baseURL = baseURL;
  }

  /**
   * Constrói a URL sem parâmetros de query
   */
  private buildDavUrl(documentOwner: string): string {
    const url = `${this.baseURL}/get-data/to-dav-mobile/${documentOwner}`;
    return url;
  }

  /**
   * Faz requisição com token direto (sem Bearer prefix)
   */
  private async tryMultipleAuthStrategies(
    url: string,
    token: string
  ): Promise<AxiosResponse<DavApiData>> {
    // Primeira tentativa sem Bearer (sabemos que funciona)
    try {
      const response = await axios.get<DavApiData>(url, {
        headers: { accept: "application/json", Authorization: token },
      });
      return response;
    } catch (originalError) {
      // Se falhar, tentar com Bearer como fallback
      try {
        const authHeader = `Bearer ${token}`;
        const response = await axios.get<DavApiData>(url, {
          headers: { accept: "application/json", Authorization: authHeader },
        });
        return response;
      } catch {
        // Se ambas falharem, propagar o erro original
        throw originalError;
      }
    }
  }

  /**
   * Busca todos os dados para um documento/empresa específico
   */
  async fetchDavData(documentOwner: string, token: string): Promise<DavApiData> {
    try {
      const url = this.buildDavUrl(documentOwner);
      const response = await this.tryMultipleAuthStrategies(url, token);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error("🚨 Erro detalhado da API DAV:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          headers: error.response?.headers,
          url: error.config?.url,
          method: error.config?.method,
        });

        const message = error.response?.data?.message || error.message;
        throw new Error(`Erro na API DAV: ${message}`);
      }

      console.error("🚨 Erro não-axios na API DAV:", error);
      throw new Error("Erro desconhecido ao acessar API DAV");
    }
  }

  /**
   * Configura o token globalmente para futuras requisições
   */
  setAuthToken(token: string): void {
    TokenManagerHelper.setAuthToken(token);
  }

  /**
   * Remove o token global
   */
  clearAuthToken(): void {
    TokenManagerHelper.clearAuthToken();
  }
}

// Instância singleton
export const davApiService = new DavApiService();

export default davApiService;
